import UserInfoFactory from './user-info';
import { getUppyCoreTranslations, getUppyDashboardTranslations } from './uppy/translations';
import { constants as config, element as elementConfig } from '@config';

export default class UppyFactory {
  constructor (opts) {
    this.id = opts.id;
    this.button = opts.button;
    this.loadtype = opts.loadtype;
    this.restrictions = opts.restrictions;
    this.serverUrl = opts.serverUrl || INKIVE.C.uppy_companion_url;
    this.onFileUploaded = opts.onFileUploaded || (() => {});
    this.onCompleted = opts.onCompleted || (() => {});
    this.onUppyInitialized = opts.onUppyInitialized || (() => {});
    this.providers = ['GoogleDrive', 'Dropbox', 'Box', 'OneDrive', 'Facebook', 'Url', 'Webcam'];
  }

  importUppyScript () {
    return new Promise(resolve => {
      if (window.Uppy) {
        resolve(window.Uppy);
        return;
      }

      $.getScript('https://releases.transloadit.com/uppy/v3.13.0/uppy.min.js').then(() => {
        resolve(window.Uppy);
      });
    });
  }

  async initUppyManager () {
    this.Uppy = await this.importUppyScript();

    try {
      this.uppy = new this.Uppy.Uppy({
        id: this.id,
        debug: false,
        autoProceed: false,
        onBeforeUpload: (files) => this._setFilesPath(files),
        locale: { strings: getUppyCoreTranslations(INKIVE.TranslateHelper.translate) }
      });

      this._setRestrictions(this.restrictions);

      this.uppy.on('file-added', async file => {
        if (this.loadtype === 'images' && !file.isRemote) {
          this._getImgDimension(file);
        }

        const projectData = await INKIVE.CompanyFactory.getProjectData();
        this.uppy.setFileMeta(file.id, { companyId: projectData.company.id });

        if (this.isVideo(file.type)) {
          const userInfo = await UserInfoFactory.get();
          const roles = userInfo.roles;
          const isFreeUser = !roles.includes('ROLE_PRO') && !roles.includes('ROLE_BUSINESS');

          try {
            await this.isVideoInsideLimit(file, isFreeUser);
          } catch (err) {
            const errorMessage = isFreeUser ? 'tool.videos.free_user_limit' : 'tool.videos.paid_user_limit';
            this.uppy.removeFile(file.id);
            this.uppy.info(INKIVE.TranslateHelper.translate(errorMessage));
            setTimeout(this.onUppyInitialized);
          }
        }
      });

      this.setUpUppy();
    } catch (err) {
      throw new Error(err);
    }
  }

  initialiseDashboard (opts = {}) {
    this.uppy
      .use(this.Uppy.Dashboard, Object.assign({
        target: 'body',
        trigger: this.button,
        inline: false,
        showProgressDetails: true,
        proudlyDisplayPoweredByUppy: false,
        locale: { strings: getUppyDashboardTranslations(INKIVE.TranslateHelper.translate) },
        thumbnailWidth: 280,
        showLinkToFileUploadResult: false,
        doneButtonHandler: () => {
          this.uppy.cancelAll();
          this.closeUppyModal();
        },
        closeModalOnClickOutside: true,
        disableThumbnailGenerator: false,
        showSelectedFiles: true,
        showRemoveButtonAfterComplete: false,
        browserBackButtonClose: false,
        theme: 'light'
      }, opts))
      .use(this.Uppy.ImageEditor, { target: this.Uppy.Dashboard })
      .use(this.Uppy.Compressor)
      .use(this.Uppy.GoldenRetriever, { serviceWorker: true });

    this._setAws3Url();
  }

  _setRestrictions (restrictions) {
    const defaultRestrictions = {
      maxFileSize: 0,
      maxNumberOfFiles: 0,
      minNumberOfFiles: 0,
      allowedFileTypes: [],
      requiredMetaFields: []
    };

    restrictions.allowedFileTypes = (restrictions.allowedFileTypes[0] === 'all')
      ? null
      : restrictions.allowedFileTypes;

    this.uppy.setOptions({ restrictions: Object.assign({}, defaultRestrictions, restrictions) });
  }

  setProviders (providers) {
    if (providers.length) {
      // providers.includes('GoogleDrive') && this._useProvider(this.Uppy.GoogleDrive, 'google_drive');
      providers.includes('Dropbox') && this._useProvider(this.Uppy.Dropbox, 'dropbox');
      providers.includes('Box') && this._useProvider(this.Uppy.Box, 'box');
      providers.includes('OneDrive') && this._useProvider(this.Uppy.OneDrive, 'onedrive');
      providers.includes('Facebook') && this._useProvider(this.Uppy.Facebook, 'facebook');
      providers.includes('Url') && this._useProvider(this.Uppy.Url, 'url');
      providers.includes('Webcam') && this._useProvider(this.Uppy.Webcam, 'webcam');
      providers.includes('ScreenCapture') && this._useProvider(this.Uppy.ScreenCapture, 'screencapture');
    } else {
      // this._useProvider(this.Uppy.GoogleDrive, 'google_drive');
      this._useProvider(this.Uppy.Dropbox, 'dropbox');
      this._useProvider(this.Uppy.Box, 'box');
      this._useProvider(this.Uppy.OneDrive, 'onedrive');
      this._useProvider(this.Uppy.Facebook, 'facebook');
      this._useProvider(this.Uppy.Url, 'url');
      this._useProvider(this.Uppy.Webcam, 'webcam');
      this._useProvider(this.Uppy.ScreenCapture, 'screencapture');
    }
  }

  close () {
    this.Uppy && this.Uppy.close();
  }

  getDashboard () {
    return this.uppy.getPlugin('Dashboard');
  }

  _setAws3Url () {
    this.uppy.use(this.Uppy.AwsS3, {
      limit: 5,
      companionUrl: this.serverUrl
    });
  }

  _setFilesPath (files) {
    const bucket = INKIVE.C.BUCKET.split('.').shift();
    const elementType = elementConfig.typesMapping[this.loadtype] ?? this.loadtype;
    const randomId = INKIVE.Helpers.generateRandomString(15).join('');
    const path = [bucket, 'company', elementType, 'original', randomId].join('/');

    const updatedFiles = Object.assign({}, files);
    Object.keys(updatedFiles).forEach(fileId => {
      updatedFiles[fileId].meta.name = encodeURI(path + '/' + INKIVE.Helpers.slugify(updatedFiles[fileId].name));
    });

    return updatedFiles;
  }

  closeUppyModal () {
    const dashboard = this.uppy.getPlugin('Dashboard');

    if (this.Uppy.Dashboard && dashboard.isModalOpen()) {
      this.uppy.cancelAll();
      dashboard.closeModal();
    }
  }

  _useProvider (provider) {
    const providerConfig = {
      target: this.Uppy.Dashboard,
      companionUrl: this.serverUrl
    };

    let providerOptions = null;
    if (provider.name === 'Webcam') {
      if (this.loadtype === 'logos' || this.loadtype === 'icons') return;
      providerOptions = this._getWebcamOptions();
    } else if (provider.name === 'ScreenCapture') {
      providerOptions = this._getScreenCaptureOptions();
    }

    const newProviderConfig = Object.assign({}, providerConfig, providerOptions || {});
    this.uppy.use(provider, newProviderConfig);
  }

  _getWebcamOptions () {
    const allowedTypes = this.restrictions.allowedFileTypes;
    const modes = [];
    let showRecordingLength = true;

    if (allowedTypes) {
      if (allowedTypes.some(type => type.includes('video'))) {
        modes.push('video-audio');
      }

      if (allowedTypes.some(type => type.includes('audio'))) {
        modes.push('audio-only');
      }

      if (allowedTypes.some(type => type.includes('image'))) {
        modes.push('picture');
        showRecordingLength = false;
      }
    }

    return {
      modes,
      mirror: false,
      showRecordingLength,
      preferredVideoMimeType: 'video/webm'
    };
  }

  _getScreenCaptureOptions () {
    return {
      preferredVideoMimeType: 'video/webm'
    };
  }

  setUpUppy () {
    this.initialiseDashboard();
    this.setProviders(this.providers);

    this.uppy
      .on('upload-success', (file, response) => {
        const payload = {
          tempLink: decodeURIComponent(response.uploadURL),
          name: file.name,
          meta: file.meta
        };

        return this.onFileUploaded(payload);
      })
      .on('complete', (result) => {
        const finish = () => {
          result.successful.length && !result.failed.length && this.closeUppyModal();
        };

        if (this._awaitMeta === 0) {
          finish();
        } else {
          const interval = setInterval(() => {
            if (this._awaitMeta === 0) {
              clearInterval(interval);

              finish();
            }
          }, 100);
        }

        return this.onCompleted(result.successful.map(payload => ({
          tempLink: decodeURIComponent(payload.uploadURL),
          name: payload.name,
          meta: payload.meta
        })));
      });
  }

  _getImgDimension (file) {
    const data = file.data; // is a Blob instance
    const url = URL.createObjectURL(data);
    const image = new Image();
    image.src = url;
    image.onload = () => {
      this.uppy.setFileMeta(file.id, {
        dimensions: {
          width: image.width,
          height: image.height
        }
      });
      URL.revokeObjectURL(url);
    };
  }

  _getVideoDuration (file) {
    return new Promise((resolve, reject) => {
      try {
        const video = document.createElement('video');
        video.src = URL.createObjectURL(file);
        video.preload = 'metadata';

        video.onloadedmetadata = () => {
          URL.revokeObjectURL(video.src);
          resolve(video.duration);
        };
      } catch (e) {
        reject(e);
      }
    });
  }

  isVideo (fileType) {
    return ['video/mp4', 'video/quicktime'].includes(fileType);
  }

  isVideoInsideLimit (file, isFreeUser) {
    const sizeLimit = isFreeUser
      ? config.videoUploadLimit.freeUser
      : config.videoUploadLimit.paidUser;

    if (file.size > sizeLimit) {
      throw new Error('VideoLimitReached');
    }

    return true;
  }
}
